import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import {
  fetchFavoritesThunk,
  addFavoriteThunk,
  removeFavoriteThunk,
} from '../store/thunks';
import FavoritesList from './FavoritesList';

const FavoritesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    favorites = [],
    loading,
    error,
  } = useSelector(
    (state: RootState) =>
      state.favorites || { favorites: [], loading: false, error: null }
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newColor, setNewColor] = useState('green');
  const [newNotes, setNewNotes] = useState('');
  const [tickerError, setTickerError] = useState('');

  // Load favorites on component mount
  useEffect(() => {
    dispatch(fetchFavoritesThunk());
  }, [dispatch]);

  // Ticker format: 1-5 uppercase letters, optional .X or -X suffix (e.g. BRK.B, RDS-A)
  const TICKER_REGEX = /^[A-Z]{1,5}([.-][A-Z]{1,3})?$/;

  const handleAddFavorite = async () => {
    const ticker = newTicker.trim().toUpperCase();
    if (!ticker) return;
    if (!TICKER_REGEX.test(ticker)) {
      setTickerError('Invalid ticker format (1-5 letters, e.g. AAPL, BRK.B)');
      return;
    }
    const existsDuplicate = (favorites || []).some(
      (f: { ticker?: string }) => (f.ticker || '').toUpperCase() === ticker
    );
    if (existsDuplicate) {
      setTickerError(`${ticker} is already in favorites`);
      return;
    }
    setTickerError('');
    try {
      await dispatch(
        addFavoriteThunk({
          ticker,
          color: newColor,
          notes: newNotes,
        })
      ).unwrap();
      setNewTicker('');
      setNewNotes('');
      setShowAddForm(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setTickerError(msg || 'Failed to add favorite');
      console.error('Error adding favorite:', error);
    }
  };

  const colorOptions = [
    {
      value: 'green',
      label: 'Green (Buy/Strong Buy)',
      icon: 'fas fa-arrow-up',
    },
    { value: 'yellow', label: 'Yellow (Watch/Hold)', icon: 'fas fa-eye' },
    { value: 'red', label: 'Red (Avoid/Sell)', icon: 'fas fa-arrow-down' },
    { value: 'blue', label: 'Gold (Sold/Exited)', icon: 'fas fa-check-circle' },
    { value: 'purple', label: 'Purple (Custom/Other)', icon: 'fas fa-star' },
  ];

  const getColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      green: 'text-success',
      yellow: 'text-warning',
      red: 'text-danger',
      blue: 'text-accent-primary',
      purple: 'text-purple',
    };
    return colorMap[color] || 'text-secondary';
  };

  return (
    <div className="favorites-tab flex min-h-0 flex-1 flex-col">
      {/* Header with Add Button and Stats */}
      <div className="row mb-2 max-md:mb-1.5 shrink-0">
        <div className="col-12">
          <div className="tv-card">
            <div className="tv-card-header md:tv-py-3 tv-py-1.5 md:tv-px-4 tv-px-3 bg-surface-container-high border-b border-border-subtle">
              <div className="tv-flex tv-justify-between tv-items-center">
                <h5 className="text-sm md:text-base font-bold m-0 text-text-primary">
                  <i className="fas fa-star me-1 md:me-2 text-[#c9a227]"></i>
                  <span className="md:hidden">Favorites ({Array.isArray(favorites) ? favorites.length : 0})</span>
                  <span className="hidden md:inline">My Favorites ({Array.isArray(favorites) ? favorites.length : 0} stocks)</span>
                </h5>
                <button
                  className="tv-btn tv-btn-primary tv-btn-sm inline-flex min-h-[44px] items-center"
                  onClick={() => setShowAddForm(!showAddForm)}
                  style={{ backgroundColor: '#c9a227', color: '#000' }}
                  aria-label="Add favorite ticker"
                >
                  <i className="fas fa-plus me-1 md:me-2"></i>
                  <span className="md:hidden">Add</span>
                  <span className="hidden md:inline">Add New</span>
                </button>
              </div>
            </div>

            {/* Add New Ticker Form */}
            {showAddForm && (
              <div className="tv-card-body tv-border-bottom tv-py-3 tv-px-4" style={{ borderBottomColor: 'var(--tv-border-primary)' }}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label htmlFor="ticker" className="text-sm font-medium text-text-secondary">
                      Ticker Symbol
                    </label>
                    <input
                      type="text"
                      className="tv-form-control"
                      id="ticker"
                      placeholder="e.g., AAPL"
                      value={newTicker}
                      maxLength={10}
                      aria-invalid={Boolean(tickerError)}
                      aria-describedby={tickerError ? 'ticker-error' : undefined}
                      onChange={(e) => {
                        setNewTicker(e.target.value.toUpperCase());
                        if (tickerError) setTickerError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddFavorite();
                      }}
                      style={{
                        backgroundColor: 'var(--tv-bg-tertiary)',
                        borderColor: tickerError ? 'var(--tv-danger, #ef4444)' : 'var(--tv-border-primary)',
                        color: 'var(--tv-text-primary)'
                      }}
                    />
                    {tickerError && (
                      <div
                        id="ticker-error"
                        role="alert"
                        className="text-xs mt-1"
                        style={{ color: 'var(--tv-danger, #ef4444)' }}
                      >
                        {tickerError}
                      </div>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="color" className="text-sm font-medium text-text-secondary">
                      Color Category
                    </label>
                    <select
                      className="tv-form-select"
                      id="color"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      style={{
                        backgroundColor: 'var(--tv-bg-tertiary)',
                        borderColor: 'var(--tv-border-primary)',
                        color: 'var(--tv-text-primary)'
                      }}
                    >
                      {colorOptions.map((option) => (
                        <option key={option.value} value={option.value} style={{ backgroundColor: 'var(--tv-bg-tertiary)', color: 'var(--tv-text-primary)' }}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="notes" className="text-sm font-medium text-text-secondary">
                      Notes (Optional)
                    </label>
                    <input
                      type="text"
                      className="tv-form-control"
                      id="notes"
                      placeholder="Entry target, reasoning, etc."
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      style={{
                        backgroundColor: 'var(--tv-bg-tertiary)',
                        borderColor: 'var(--tv-border-primary)',
                        color: 'var(--tv-text-primary)'
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="notes-spacer" className="text-sm font-medium text-text-secondary invisible">
                      &nbsp;
                    </label>
                    <div className="tv-flex tv-gap-2">
                      <button
                        className="tv-btn tv-btn-success tv-btn-sm"
                        onClick={handleAddFavorite}
                        disabled={!newTicker.trim()}
                        aria-label="Add favorite"
                        title="Add favorite"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        className="tv-btn tv-btn-secondary tv-btn-sm"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewTicker('');
                          setNewNotes('');
                        }}
                        aria-label="Cancel adding favorite"
                        title="Cancel adding favorite"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Color Legend - Hidden on mobile, shown on desktop */}
            <div className="hidden md:block tv-card-body tv-py-2 tv-px-3 md:tv-py-3 md:tv-px-4">
              <div className="row">
                <div className="col-12">
                  <small className="tv-text-secondary text-xs md:text-sm">Color Categories:</small>
                  <div className="tv-flex tv-flex-wrap tv-gap-2 md:tv-gap-3 tv-mt-1 md:tv-mt-2">
                    {colorOptions.map((option) => (
                      <span
                        key={option.value}
                        className="tv-flex tv-items-center"
                      >
                        <i
                          className={`${option.icon} ${getColorClass(
                            option.value
                          )} me-1 text-xs`}
                        ></i>
                        <small className="tv-text-secondary text-xs">{option.label}</small>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Favorites List */}
      {loading ? (
        <div className="row">
          <div className="col-12">
            <div className="tv-card">
              <div className="tv-card-body tv-text-center tv-py-5">
                <div
                  className="spinner-border"
                  style={{ color: 'var(--accent-primary)' }}
                  role="status"
                  aria-live="polite"
                >
                  <span className="visually-hidden">Loading favorites...</span>
                </div>
                <p className="tv-mt-3 tv-text-secondary">Loading your favorites...</p>
              </div>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="row">
          <div className="col-12">
            <div className="tv-alert tv-alert-danger" style={{ 
              backgroundColor: 'var(--tv-bg-tertiary)', 
              borderColor: 'var(--tv-accent-red)',
              color: 'var(--tv-accent-red)'
            }}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              Error loading favorites: {error}
              <button
                className="tv-btn tv-btn-outline tv-btn-sm tv-ml-3"
                onClick={() => dispatch(fetchFavoritesThunk())}
                style={{ 
                  borderColor: 'var(--tv-accent-red)',
                  color: 'var(--tv-accent-red)'
                }}
              >
                <i className="fas fa-redo me-1"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : !Array.isArray(favorites) || favorites.length === 0 ? (
        <div className="row">
          <div className="col-12">
            <div className="tv-card">
              <div className="tv-card-body tv-text-center tv-py-5">
                <i className="fas fa-star-o fa-3x tv-text-secondary tv-mb-3"></i>
                <h5 className="text-base font-semibold text-text-primary mb-3">No Favorites Yet</h5>
                <p className="tv-text-secondary tv-mb-4">
                  Add stocks to your favorites to track them here. You can add
                  stocks from the screening results or manually add any ticker
                  using the "Add New" button above.
                </p>
                <button
                  className="tv-btn tv-btn-primary inline-flex min-h-[44px] items-center justify-center gap-2 px-5 py-2.5"
                  onClick={() => setShowAddForm(true)}
                  style={{ backgroundColor: 'var(--accent-primary)', color: 'var(--on_primary)' }}
                >
                  <i className="fas fa-plus"></i>
                  Add Your First Favorite
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <FavoritesList
            favorites={Array.isArray(favorites) ? favorites : []}
            onRemove={async (ticker) => {
              try {
                await dispatch(removeFavoriteThunk(ticker)).unwrap();
              } catch (error) {
                console.error('Error removing favorite:', error);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FavoritesTab;
